import axios from 'axios';
import { AuthService } from './auth.service';
import { ENDPOINTS } from '../constants/endpoints';
import { Attendance, AttendanceResponse } from '../interfaces/attendance.interface';
import { format } from 'date-fns';
import { readPunchLocation } from '../utils/geolocation';

export const AttendanceService = {
    getLoggedInUserAttendanceList: async (startDate?: Date, endDate?: Date): Promise<{ data: AttendanceResponse; hasTodayAttendance: boolean }> => {
        const params: any = { page: 0, size: 100 };

        if (startDate) {
            params.startDate = format(startDate, 'yyyy-MM-dd');
        }

        if (endDate) {
            params.endDate = format(endDate, 'yyyy-MM-dd');
        }

        const response = await axios.get(ENDPOINTS.ATTENDANCE.LOGGED_IN_USER_ATTENDANCE, {
            params,
            headers: AuthService.getAuthHeader()
        });

        const today = format(new Date(), 'yyyy-MM-dd');
        const hasTodayAttendance = response.data.content.some(
            (attendance: { date: string }) => attendance.date === today
        );

        return {
            data: response.data,
            hasTodayAttendance
        };
    },

    clockIn: async (): Promise<void> => {
        // readPunchLocation() never rejects and never blocks the punch: on any
        // failure (denied permission, no GPS, timeout, insecure context) it
        // resolves undefined and the punch proceeds with an empty body, which
        // the backend records as NO_LOCATION.
        const location = await readPunchLocation();
        await axios.post(ENDPOINTS.ATTENDANCE.CLOCK_IN, location ?? {}, {
            headers: {
                ...AuthService.getAuthHeader(),
                'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
            }
        });
    },

    clockOut: async (): Promise<void> => {
        const location = await readPunchLocation();
        await axios.post(ENDPOINTS.ATTENDANCE.CLOCK_OUT, location ?? {}, {
            headers: {
                ...AuthService.getAuthHeader(),
                'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
            }
        });
    },

    getEmployeeAttendanceList: async (
        employeeUserId: string, startDate: Date, endDate: Date
    ): Promise<AttendanceResponse> => {
        const params = {
            page: 0, size: 100,
            startDate: format(startDate, 'yyyy-MM-dd'),
            endDate: format(endDate, 'yyyy-MM-dd')
        };
        const response = await axios.get(
            ENDPOINTS.ATTENDANCE.EMPLOYEE_ATTENDANCE(employeeUserId),
            { params, headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    getInstitutionTodayAttendance: async (): Promise<Attendance[]> => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(
            ENDPOINTS.ATTENDANCE.INSTITUTION_TODAY(institutionId),
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    // Who Today's Attendance should list: restricted=true for a department head (their team) or an
    // own-scoped caller; restricted=false means keep the full member list.
    getInstitutionTodayRoster: async (): Promise<{ restricted: boolean; userIds: string[] }> => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(
            ENDPOINTS.ATTENDANCE.INSTITUTION_TODAY_ROSTER(institutionId),
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    updateAttendance: async (id: string, startTime: string, endTime: string): Promise<Attendance> => {
        const response = await axios.put(
            ENDPOINTS.ATTENDANCE.UPDATE(id),
            { startTime, endTime },
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    downloadMonthlyReport: async (startDate: Date, endDate: Date): Promise<void> => {
        const institutionId = AuthService.getSelectedInstitution();
        const params = {
            startDate: format(startDate, 'yyyy-MM-dd'),
            endDate: format(endDate, 'yyyy-MM-dd'),
        };
        const response = await axios.get(
            ENDPOINTS.ATTENDANCE.MONTHLY_REPORT(institutionId),
            {
                params,
                headers: AuthService.getAuthHeader(),
                responseType: 'blob',
            }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download',
            `attendance-report-${format(startDate, 'yyyy-MM-dd')}-to-${format(endDate, 'yyyy-MM-dd')}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    },

    markLocationReviewed: async (id: string): Promise<Attendance> => {
        const response = await axios.patch(ENDPOINTS.ATTENDANCE.LOCATION_REVIEW(id), {}, {
            headers: AuthService.getAuthHeader(),
        });
        return response.data;
    },
};
